import type { LucideIcon } from 'lucide-react'
import type { NavGroup, NavItem, NavSubgroup, SidebarEntry } from '@/constants/navigation'
import { NAV_SECTIONS } from '@/constants/nav-sections'

export type NavGroupEntry = NavGroup & { type: 'group' }
export type NavItemEntry = NavItem & { type: 'item' }

export interface ActiveNav {
  /** Active leaf id (item or group child), '' when no nav page matches. */
  id: string
  /** Owning group id when the active leaf is a group child, else ''. */
  groupId: string
  /** Sub-section id inside that group (e.g. 'products'), else ''. */
  subId: string
}

export interface NavGroupParts {
  /** Children drawn directly in the group. */
  flat: NavItem[]
  /** Folding sub-sections (only those with visible items), in `subgroups` order. */
  subs: Array<{ sub: NavSubgroup; items: NavItem[] }>
}

export interface NavSectionView {
  id: string
  label: string
  entries: SidebarEntry[]
}

export interface NavPage {
  id: string
  label: string
  href: string
  icon: LucideIcon
  /** Group (› sub-section) label for grouped pages ('' for top-level items). */
  group: string
}

export interface NavCrumb {
  label: string
  href?: string
}

/** Split a group's (permission-filtered) children into flat items + sub-sections.
 * An item whose `subgroup` id is not declared on the group stays flat. */
export function splitGroupChildren(group: NavGroup): NavGroupParts {
  const declared = new Set((group.subgroups ?? []).map((s) => s.id))
  const flat = group.children.filter((c) => !c.subgroup || !declared.has(c.subgroup))
  const subs = (group.subgroups ?? [])
    .map((sub) => ({ sub, items: group.children.filter((c) => c.subgroup === sub.id) }))
    .filter((s) => s.items.length > 0)
  return { flat, subs }
}

function subIdOf(group: NavGroup, child: NavItem): string {
  return child.subgroup && (group.subgroups ?? []).some((s) => s.id === child.subgroup) ? child.subgroup : ''
}

/** Longest-prefix score: exact or `href/…` match; '/' only matches itself. */
function matchScore(pathname: string, href: string): number {
  if (href === '/') return pathname === '/' ? 1 : 0
  return pathname === href || pathname.startsWith(`${href}/`) ? href.length : 0
}

/** Active leaf + group for a pathname (most specific href wins). */
export function findActiveNav(entries: SidebarEntry[], pathname: string): ActiveNav {
  let best: ActiveNav & { score: number } = { id: '', groupId: '', subId: '', score: 0 }
  for (const entry of entries) {
    if (entry.type === 'item') {
      const score = matchScore(pathname, entry.href)
      if (score > best.score) best = { id: entry.id, groupId: '', subId: '', score }
      continue
    }
    for (const child of entry.children) {
      const score = matchScore(pathname, child.href)
      if (score > best.score) best = { id: child.id, groupId: entry.id, subId: subIdOf(entry, child), score }
    }
  }
  return { id: best.id, groupId: best.groupId, subId: best.subId }
}

/** NAV_SECTIONS resolved against the permission-filtered entries (empty sections dropped). */
export function buildNavSections(entries: SidebarEntry[]): NavSectionView[] {
  const byId = new Map(entries.map((entry) => [entry.id, entry]))
  return NAV_SECTIONS.map((section) => ({
    id: section.id,
    label: section.label,
    entries: section.entryIds
      .map((id) => byId.get(id))
      .filter((entry): entry is SidebarEntry => Boolean(entry)),
  })).filter((section) => section.entries.length > 0)
}

/** Every reachable page, flattened for the Ctrl+K palette. */
export function flattenNavPages(entries: SidebarEntry[]): NavPage[] {
  return entries.flatMap((entry) =>
    entry.type === 'group'
      ? entry.children.map((child) => {
          const sub = (entry.subgroups ?? []).find((s) => s.id === subIdOf(entry, child))
          return {
            id: child.id,
            label: child.label,
            href: child.href,
            icon: child.icon,
            group: sub ? `${entry.label} › ${sub.label}` : entry.label,
          }
        })
      : [{ id: entry.id, label: entry.label, href: entry.href, icon: entry.icon, group: '' }],
  )
}

/** Case-insensitive match on "group › sub label"; every word must appear. */
export function filterNavPages(pages: NavPage[], query: string): NavPage[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (words.length === 0) return pages
  return pages.filter((page) => {
    const hay = `${page.group.replace('›', ' ')} ${page.label}`.toLowerCase()
    return words.every((word) => hay.includes(word))
  })
}

/**
 * Breadcrumbs for the desktop top bar: [group] › page › [sub-page title].
 * `title` is the resolved page title (resolvePageTitleFromPath / chrome title).
 */
export function buildNavCrumbs(
  entries: SidebarEntry[],
  active: ActiveNav,
  pathname: string,
  title: string,
): NavCrumb[] {
  const crumbs: NavCrumb[] = []
  const group = active.groupId
    ? (entries.find((entry) => entry.id === active.groupId) as NavGroupEntry | undefined)
    : undefined
  const leaf = group
    ? group.children.find((child) => child.id === active.id)
    : (entries.find((entry) => entry.id === active.id && entry.type === 'item') as NavItemEntry | undefined)

  if (group && group.label !== leaf?.label) crumbs.push({ label: group.label })
  const sub = group && active.subId ? (group.subgroups ?? []).find((s) => s.id === active.subId) : undefined
  if (sub) crumbs.push({ label: sub.label })
  if (leaf) crumbs.push({ label: leaf.label, href: leaf.href })
  const onLeaf = Boolean(leaf && pathname === leaf.href)
  if (title && (!leaf || (!onLeaf && title !== leaf.label))) crumbs.push({ label: title })
  return crumbs
}
